import React, { useCallback, useMemo } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advanced from 'dayjs/plugin/advancedFormat'
import { Tooltip, makeStyles } from '@material-ui/core'
import Icon from '../../../atoms/Icon'
import Avatar from '../../../atoms/Avatar'
import { ICON_NAMES } from '../../../../constants'

dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(advanced)

const MAX_VISIBLE_ATTENDEES = 3

const useStyles = makeStyles(() => ({
  // Default variant — unchanged from original
  meeting: {
    background: 'var(--theme-color-surface-d0)',
    borderRadius: 'var(--border-radius-md)',
    padding: '0.875rem 1.5rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    border: 'var(--border-neutral-thin)'
  },
  meetingIcon: {
    marginRight: '1rem'
  },
  name: {
    fontSize: '1rem',
    fontWeight: '600'
  },
  meetingTime: {
    '& > *': {
      fontSize: '0.8rem',
      color: 'var(--text-color-secondary)',
      marginRight: '.5rem'
    }
  },
  clickableMeeting: {
    cursor: 'pointer',
    transition: 'border 0.3s linear'
  },

  // Compact variant — new styling for the redesigned card
  meetingCompact: {
    background: 'var(--color-surface-050)',
    borderRadius: 'var(--border-radius-md)',
    padding: '0.75rem 1rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.75rem',
    borderLeft: '3px solid var(--color-primary-400)'
  },
  compactBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
    minWidth: 0
  },
  compactName: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: 'var(--text-color-main)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  compactTime: {
    fontSize: '0.75rem',
    color: 'var(--text-color-subtle)'
  },
  attendees: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  attendeeAvatar: {
    marginLeft: '-0.375rem',
    border: '2px solid var(--theme-color-surface-d0)',
    borderRadius: '50%',
    '&:first-child': {
      marginLeft: 0
    }
  }
}))

const Meeting = ({ meeting, linkBaseUrl, variant, showAttendees }) => {
  const classes = useStyles()

  const { start, end, date, timezone, meetingId, url } = useMemo(() => {
    const timeFormat = variant === 'compact' ? 'h:mma' : 'h:mm'
    return {
      meetingId: meeting.meetingId,
      date: dayjs(meeting.startDate).format('ddd M/DD'),
      start: dayjs(meeting.startDate).format(timeFormat),
      end: dayjs(meeting.endDate).format(timeFormat),
      timezone: dayjs(meeting.endDate).format('z'),
      url: linkBaseUrl ? `${linkBaseUrl}/${meeting.meetingId}/view` : null
    }
  }, [meeting, linkBaseUrl, variant])

  const isClickable = useMemo(
    () => meetingId && linkBaseUrl,
    [meetingId, linkBaseUrl]
  )

  const onClickMeetingHandler = useCallback(() => {
    if (isClickable) {
      window.open(url, '_blank', 'noreferrer')
    }
  }, [isClickable, url])

  // Compact variant rendering
  if (variant === 'compact') {
    // Prefer meeting.subject with fallback to client.longName
    const title = meeting?.subject || meeting?.client?.longName || ''

    // Handle n attendees with cap + overflow badge
    const attendees = meeting?.attendees || []
    const visibleAttendees = attendees.slice(0, MAX_VISIBLE_ATTENDEES)
    const overflowCount = Math.max(0, attendees.length - MAX_VISIBLE_ATTENDEES)

    return (
      <Tooltip title={meeting.subject}>
        <div
          className={clsx(
            classes.meetingCompact,
            isClickable ? classes.clickableMeeting : null
          )}
          onClick={onClickMeetingHandler}
        >
          <div className={classes.compactBody}>
            <div className={classes.compactName}>{title}</div>
            <div className={classes.compactTime}>{start}-{end}</div>
          </div>
          {showAttendees && attendees.length > 0 && (
            <div className={classes.attendees}>
              {visibleAttendees.map((attendee, idx) => (
                <div key={attendee.id || idx} className={classes.attendeeAvatar}>
                  <Avatar
                    customSize='1.75rem'
                    customFontSize='0.75rem'
                    avatarLetters={attendee.fullName || ''}
                    src={attendee.profilePictureUrl}
                    isPublicResource
                    useAbbreviation={false}
                  />
                </div>
              ))}
              {overflowCount > 0 && (
                <div className={classes.attendeeAvatar}>
                  <Avatar
                    customSize='1.75rem'
                    customFontSize='0.75rem'
                    avatarLetters={`+${overflowCount}`}
                    useAbbreviation
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </Tooltip>
    )
  }

  // Default variant — unchanged
  return (
    <Tooltip title={meeting.subject}>
      <div
        className={clsx(
          classes.meeting,
          isClickable ? classes.clickableMeeting : null
        )}
        onClick={onClickMeetingHandler}
      >
        <div className={classes.meetingIcon}>
          <Icon name={ICON_NAMES.calendar} customSize='1.5rem' />
        </div>
        <div>
          <div className={classes.name}>{meeting?.client?.longName || ''}</div>
          <div className={classes.meetingTime}>
            <span>{date}</span>
            <span>
              {start} - {end} {timezone}
            </span>
          </div>
        </div>
      </div>
    </Tooltip>
  )
}

Meeting.propTypes = {
  meeting: PropTypes.object.isRequired,
  linkBaseUrl: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'compact']),
  showAttendees: PropTypes.bool
}

Meeting.defaultProps = {
  linkBaseUrl: null,
  variant: 'default',
  showAttendees: false
}

export default Meeting
